import {
  Component,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
  Input,
  ChangeDetectorRef
} from '@angular/core';
import { combineLatest, map, Observable } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';

import { AppFacade } from '@app/app.facade';
import { ANIMATIONS, TIME_TO_SLIDE } from '@app/constants/animations.constants';
import { MODAL_HEIGHT } from '@app/constants/modal-heights';
import { FilterMagicStyle } from '@shared/magic-filter-bar/entities/filter-magic-style';
import {
  HeaderWidget, HeaderWidgetConfig, HeaderWidgetType
} from '@design-system/components/templates/header/entities/header.types';
import { BackgroundSurfaceType } from '@design-system/components/atoms/background-surface/entities/background-surface.types';

@Component({
  selector: 'app-mobile-filter-magic-cont',
  templateUrl: './mobile-filter-magic-cont.component.html',
  styleUrls: ['./mobile-filter-magic-cont.component.sass'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class MobileFilterMagicContComponent {

  @Input() hasFilterSelected = false;
  @Input() title = 'FILTER';
  @Input() heightClass = MODAL_HEIGHT.MEDIUM;
  @Input() titleProps = {};
  @Input() styleByContext = FilterMagicStyle.NoneStyle;
  @Input() individualFilter: boolean = false;

  @Output() clear: EventEmitter<void> = new EventEmitter<void>();

  public animation = ANIMATIONS.SLIDE_IN_BOTTOM;
  public fadeOut = false;

  public readonly surface = BackgroundSurfaceType.Primary;

  constructor(
    private readonly appFacade: AppFacade,
    private readonly translateService: TranslocoService,
    private readonly cdr: ChangeDetectorRef
  ) { }

  get headerWidgets$(): Observable<HeaderWidgetConfig[]> {
    return combineLatest([
      this.titleWithTranslation$, this.translateService.selectTranslate('CLEAN')
    ]).pipe(
      map(([title, cleanLabel]) => {
        return [
          this.isNoneStyle && {
            Type: HeaderWidgetType.Back,
          },
          {
            Type: HeaderWidgetType.Title,
            Label: title,
            LabelAlign: this.isNoneStyle ? 'center' : 'left'
          },
          this.hasFilterSelected && {
            Type: HeaderWidgetType.Clean,
            Label: cleanLabel
          },
          !this.isNoneStyle && {
            Type: HeaderWidgetType.Close,
          },
        ];
      })
    );
  }

  get titleWithTranslation$(): Observable<string> {
    return this.translateService.selectTranslate(this.title, this.titleProps);
  }

  private get isNoneStyle(): boolean {
    return this.styleByContext === FilterMagicStyle.NoneStyle;
  }

  get boxShadow(): boolean {
    return this.heightClass === MODAL_HEIGHT.FULL;
  }

  get isStyleOne(): boolean {
    return this.styleByContext === FilterMagicStyle.StyleOne;
  }

  get isStyleTwo(): boolean {
    return this.styleByContext === FilterMagicStyle.StyleTwo;
  }

  get showApplyButton(): boolean {
    return this.isNoneStyle || this.isStyleTwo;
  }

  get showFloatingCleaner(): boolean {
    return this.isNoneStyle;
  }

  get showFixedCleaner(): boolean {
    return this.isStyleOne || this.isStyleTwo;
  }

  public widgetAction(widget: HeaderWidget): void {
    if (widget.Type === HeaderWidgetType.Close || widget.Type === HeaderWidgetType.Back) {
      this.close();
    } else if (widget.Type === HeaderWidgetType.Clean) {
      this.clear.emit();
    }
  }

  public close(): void {
    this.animation = ANIMATIONS.SLIDE_OUT_BOTTOM;
    this.fadeOut = true;
    this.cdr.detectChanges();
    setTimeout(() => {
      this.appFacade.navigateToLastRoute();
    }, TIME_TO_SLIDE);
  }

}
